import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import RadialGauge from '@/components/base/RadialGauge';
import SolarArrayViz from '@/components/base/SolarArrayViz';
import { SectionHead, MetricStack } from '@/components/base/MetricPanel';
import {
  AlertTriangle, BarChart3, Cpu, Gauge, Loader, RefreshCw, ShieldCheck,
  SignalHigh, TrendingDown, TrendingUp, Zap, type LucideIcon,
} from 'lucide-react';
import InverterGroupCard, { VerticalScale } from './InverterGroupCard';
import type { InverterTelemetryResponse, InverterItem } from './inverterTelemetryTypes';
import { inverterTelemetryMock } from '@/mocks/inverterTelemetry';

interface Props {
  assetId: string;
}

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes

const STATE_COLORS: Record<string, string> = {
  generating: 'var(--ok)',
  standby_or_waiting: 'var(--text-dim)',
  normal_no_power: 'var(--text-muted)',
};

// Підписи стану — через i18n, а не зашиті рядки: раніше вони лишались
// англійськими в усіх мовах (видно було лише на майданчиках з однією
// групою інверторів, бо тільки там рендериться ця картка).
const STATE_LABEL_KEYS: Record<string, string> = {
  generating: 'inv_tel_state_generating',
  standby_or_waiting: 'inv_tel_state_standby',
  normal_no_power: 'inv_tel_state_no_power',
};

function fmtKw(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(1);
}

function fmtPct(v: number | null): string {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtDelta(v: number | null, pct: number | null): string {
  if (v == null || pct == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} kW (${sign}${pct.toFixed(1)}%)`;
}

function fmtTime(ts: string | null): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

function getGaugeColor(inv: InverterItem): string {
  if (inv.qualityFlag === 'no_data' || inv.qualityFlag.startsWith('read_error') || inv.qualityFlag.startsWith('invalid_')) {
    return 'var(--accent)';
  }
  return STATE_COLORS[inv.operationalState] ?? 'var(--text-muted)';
}

function isMuted(inv: InverterItem): boolean {
  return inv.qualityFlag === 'no_data' || inv.pacKw == null;
}

// Картка одного інвертора — той самий каркас, що й InverterGroupCard
// (чіп-іконка + назва, статус праворуч, число та % номіналу зліва,
// вертикальна шкала справа). Раніше тут був круглий гейдж із зашитими
// англійськими підписами, через що майданчики з однією групою (напр.
// Табаки) виглядали й читались інакше, ніж решта станцій.
function InverterCard({ inv }: { inv: InverterItem }) {
  const { t } = useTranslation();
  const color = getGaugeColor(inv);
  const muted = isMuted(inv);
  const stateKey = STATE_LABEL_KEYS[inv.operationalState];
  const stateLabel = stateKey ? t(stateKey) : inv.operationalState;
  const pct = Math.max(0, Math.min(100, inv.percentOfNominal ?? 0));
  const title = `${t('inv_tel_unit')} ${inv.unitId}`;

  return (
    <div className="tile p-3.5 flex flex-col" style={{ gap: 11 }}>
      {/* Дзеркальна шапка: статус зліва, назва інвертора праворуч. */}
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center shrink-0" style={{ gap: 5, fontSize: 10.5, color }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: color, display: 'inline-block' }} />
          {stateLabel}
        </span>
        <span className="inline-flex items-center min-w-0" style={{ gap: 8 }}>
          <span style={{ width: 27, height: 27, borderRadius: 8, background: 'var(--surface-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Cpu size={15} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
          </span>
          <span className="font-display truncate" style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }} title={title}>{title}</span>
        </span>
      </div>

      {inv.qualityFlag !== 'ok' && (
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded self-start"
          style={{ background: 'var(--surface-2)', color: 'var(--text-dim)' }}
        >
          {inv.qualityFlag}
        </span>
      )}

      {/* Шкала зліва, одразу за нею значення, підписи — при правому краї. */}
      <div className="flex items-center" style={{ gap: 10 }}>
        <VerticalScale value={muted ? null : pct} max={100} color={color} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-baseline justify-between">
            <span className="font-display" style={{ fontSize: 19, fontWeight: 700, color: muted ? 'var(--text-dim)' : 'var(--text)', lineHeight: 1 }}>
              {fmtKw(inv.pacKw)}<span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}> кВт</span>
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('inv_tel_power')}</span>
          </div>
          <div className="flex items-baseline justify-between" style={{ marginTop: 5 }}>
            <span className="font-display" style={{ fontSize: 13, fontWeight: 600, color }}>
              {inv.percentOfNominal != null && !muted ? fmtPct(inv.percentOfNominal) : '—'}
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('inv_tel_pct_nominal')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InverterTelemetryWidget({ assetId }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<InverterTelemetryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const cardBg = 'var(--surface-solid)';
  const cardBorder = 'var(--line)';
  const textMain = 'var(--text)';
  const textMuted = 'var(--text-muted)';
  const kpiBg = 'var(--surface-2)';
  const innerBg = 'var(--surface-2)';
  const innerBorder = 'var(--line)';
  const innerCardBg = 'var(--surface-solid)';
  const iconBg = 'var(--surface-2)';
  const badgeMutedBg = 'var(--surface-2)';
  const mutedText = 'var(--text-muted)';
  const pendingBg = 'color-mix(in srgb, var(--warn) 14%, transparent)';
  const pendingText = 'var(--warn)';
  const forecastBg = 'color-mix(in srgb, var(--series-3) 12%, transparent)';
  const footerBorder = 'var(--line)';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<InverterTelemetryResponse>(
        `/forecasting/assets/${assetId}/inverters/latest`
      );
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setData(null);
        setLastRefresh(new Date());
      } else {
        setError(getApiErrorMessage(err, 'Failed to load inverter telemetry'));
      }
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchData]);

  const totalPacKw = data?.totalPacKw ?? data?.totals?.totalPacKw ?? null;
  const pctOfNominal = data?.percentOfNominal ?? data?.totals?.percentOfNominal ?? null;
  const pctOfEffectiveCap = data?.percentOfEffectiveCap ?? null;
  const effectiveCapKw = data?.effectiveCapKw ?? null;
  const curtailmentPct = data?.curtailmentPct ?? null;
  const plantNominalKw = data?.nominal?.plantKw ?? 1000;
  const gaugeMax = effectiveCapKw ?? plantNominalKw;
  const gaugePct = pctOfEffectiveCap ?? pctOfNominal;
  const groups = data?.groups ?? [];
  const forecast = data?.forecast;
  const comparison = data?.comparison;
  const inverters = data?.inverters ?? [];

  // §3: відхилення вниз — це УВАГА (--warn), а не аварія. --err лишається за
  // реальною відмовою, --accent — за активними елементами інтерфейсу.
  const deltaColor =
    comparison?.deltaKw == null
      ? textMuted
      : comparison.deltaKw < 0
        ? 'var(--warn)'
        : 'var(--ok)';
  // Покриття рахуємо за ФАКТОМ доступних інверторів, а не за рядком coverage:
  // бекенд віддає його у вільній формі («64 / 64»), і перевірка на 'full'
  // не спрацьовувала — 64/64 світилось червоним, хоча це повна норма.
  // §3: повне покриття — --ok, неповне — увага (--warn), нуль зв'язку — --err.
  const covAvail = data?.availableInverterCount ?? null;
  const covExpect = data?.expectedInverterCount ?? null;
  const coverageColor =
    covAvail == null || covExpect == null ? textMuted
      : covAvail >= covExpect ? 'var(--ok)'
        : covAvail > 0 ? 'var(--warn)'
          : 'var(--err)';
  const qualityColor = data?.qualityFlag === 'ok' ? 'var(--ok)' : 'var(--warn)';

  // Віджет більше не має власної зовнішньої картки: кожен блок усередині —
  // самостійна плитка .tile, як у EMS Box. Саме дві вкладені картки й
  // створювали відчуття великої порожньої рамки. Кнопку оновлення
  // перенесено в рядок заголовка секції.
  return (
    <div>

      {/* Error state */}
      {error && (
        <div className="p-6 text-center mb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: iconBg }}
          >
            <AlertTriangle size={20} strokeWidth={2} style={{ color: 'var(--err)' }} />
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>
            {t('inv_tel_error')}
          </p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>
            {error}
          </p>
          <button
            onClick={fetchData}
            className="mt-3 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ background: 'color-mix(in srgb, var(--text) 10%, transparent)', border: '1px solid var(--line-strong)', color: 'var(--text)' }}
          >
            {t('inv_tel_retry')}
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && !data && (
        <div className="text-center py-8">
          <Loader size={20} strokeWidth={2} className="animate-spin" style={{ color: textMuted }} />
          <p className="text-xs mt-2" style={{ color: textMuted }}>
            Loading inverter telemetry...
          </p>
        </div>
      )}

      {/* Pending telemetry — Modbus not yet configured */}
      {!error && data && data.telemetryStatus === "pending" && (
        <div className="text-center py-10 px-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: pendingBg }}>
            <Zap size={22} strokeWidth={1.8} style={{ color: pendingText }} />
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: textMain }}>{t('inv_tel_pending')}</p>
          <p className="text-xs max-w-md mx-auto" style={{ color: textMuted }}>
            {data.message || "Asset is forecast-only until site operator enables Modbus data collection."}
          </p>
          {data.forecast?.forecastCurrentKw != null && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: forecastBg }}>
              <BarChart3 size={15} strokeWidth={2} style={{ color: 'var(--series-3)' }} />
              <span className="text-xs" style={{ color: textMuted }}>{t('inv_tel_forecast')}</span>
              <span className="text-sm font-semibold" style={{ color: 'var(--series-3)' }}>
                {data.forecast.forecastCurrentKw.toFixed(1)} kW
              </span>
            </div>
          )}
        </div>
      )}

      {!error && data && data.telemetryStatus !== "pending" && (
        <>

          {/* ── Головний блок: три чіткі зони в одній плитці ──────────────────
              Дані ліворуч, гейдж по центру, тематичний малюнок праворуч,
              зсунутий трохи лівіше (менший maxWidth плюс від'ємний правий
              відступ), щоб не тулився у сам край. Раніше порядок був
              гейдж-дані-малюнок; дані на початку читаються природніше
              (спершу числа, далі візуалізація), а сама композиція стала
              компактнішою (менші відступи й розмір гейджа). */}
          <div className="tile" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <SectionHead Icon={Zap} title={t('inv_tel_title')} hint={t('inv_tel_subtitle')} />
              <button
                onClick={fetchData}
                disabled={loading}
                title={t('inv_tel_refresh')}
                aria-label={t('inv_tel_refresh')}
                className="flex items-center justify-center cursor-pointer shrink-0 transition-colors"
                style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--surface-2)', color: 'var(--text-dim)' }}
              >
                {loading ? <Loader size={13} strokeWidth={2} className="animate-spin" /> : <RefreshCw size={13} strokeWidth={2} />}
              </button>
            </div>

            {/* Grid 1fr/auto/1fr замість flex: ліва й права зони рівної
                ширини (1fr кожна), тож гейдж у середній auto-колонці стоїть
                строго по центру плитки незалежно від того, скільки місця
                фактично займають метрики чи малюнок. Flex із різними
                flex-basis для лівого й правого блоку саме через цю
                асиметрію й зміщував кільце вбік. На вузьких екранах — одна
                колонка (малюнок все одно прихований до xl). */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center" style={{ gap: 20 }}>
              {/* 1) Дані — зліва, сіткою 2×2 (по два показники в рядок);
                  у кожному показнику іконка ліворуч, підпис і значення
                  праворуч від неї. */}
              <MetricStack
                layout="grid"
                items={[
                  {
                    Icon: BarChart3,
                    label: t('inv_tel_forecast'),
                    value: forecast?.forecastCurrentKw != null ? fmtKw(forecast.forecastCurrentKw) : '—',
                    unit: forecast?.forecastCurrentKw != null ? 'кВт' : undefined,
                  },
                  {
                    Icon: comparison?.deltaKw != null && comparison.deltaKw < 0 ? TrendingDown : TrendingUp,
                    label: t('inv_tel_delta'),
                    value: fmtDelta(comparison?.deltaKw, comparison?.deltaPct),
                    color: deltaColor,
                  },
                  {
                    Icon: Cpu,
                    label: t('inv_tel_inverters'),
                    value: `${data.availableInverterCount ?? '—'}/${data.expectedInverterCount ?? '—'}`,
                  },
                  {
                    Icon: SignalHigh,
                    label: t('inv_tel_coverage'),
                    value: data.coverage,
                    color: coverageColor,
                  },
                ]}
              />

              {/* 2) Гейдж — по центру середньої колонки. Колір нейтральний:
                  §3 не фарбує поточний стан, лише відхилення й статуси. */}
              <div className="justify-self-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <RadialGauge
                  value={totalPacKw}
                  max={gaugeMax}
                  unit="кВт AC"
                  sub={`${t('inv_tel_total')} · ${fmtPct(gaugePct)}`}
                  color="var(--text)"
                  size={224}
                  valueScale={0.19}
                />
                {curtailmentPct != null && curtailmentPct < 100 && (
                  <div
                    style={{
                      marginTop: 6, padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 500,
                      background: 'color-mix(in srgb, var(--warn) 14%, transparent)', color: 'var(--warn)',
                    }}
                    title={`${t('inv_tel_cap')}: ${effectiveCapKw} kW`}
                  >
                    {t('inv_tel_curtailed', { pct: curtailmentPct })}
                  </div>
                )}
              </div>

              {/* 3) Тематичний візуал — більший і по центру своєї колонки
                  (пропорційно, а не притиснутий до краю): SVG сам тримає
                  співвідношення сторін (height: auto за viewBox), тож ширша
                  колонка просто масштабує малюнок, а не спотворює його.
                  Частина полотна плитки, а не окрема картка: розчиняється
                  маскою, щоб не читатись як вставлена картинка й не
                  конкурувати з даними. Ховається на вузьких екранах. */}
              <div
                className="hidden xl:flex justify-self-center"
                style={{
                  width: '100%', maxWidth: 400, alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none', opacity: 0.55,
                  WebkitMaskImage: 'linear-gradient(to left, #000 45%, transparent 100%), linear-gradient(to top, #000 55%, transparent 100%)',
                  maskImage: 'linear-gradient(to left, #000 45%, transparent 100%), linear-gradient(to top, #000 55%, transparent 100%)',
                  WebkitMaskComposite: 'source-in',
                  maskComposite: 'intersect',
                }}
              >
                <SolarArrayViz frac={gaugePct != null ? gaugePct / 100 : 0} />
              </div>
            </div>

            {/* Службовий рядок на всю ширину плитки — час, якість, обмеження. */}
            <div
              style={{
                flexBasis: '100%', marginTop: 4, paddingTop: 12, display: 'flex', gap: 16,
                flexWrap: 'wrap', alignItems: 'center', fontSize: 11.5,
                borderTop: '1px solid var(--line)', color: 'var(--text-dim)',
              }}
            >
              <span className="inline-flex items-center gap-1.5" title={t('inv_tel_last_update')}>
                <RefreshCw size={12} strokeWidth={2} />
                <span className="tabular-nums">{data.readAtLocal ?? '—'}</span>
              </span>
              <span className="inline-flex items-center gap-1.5" title={t('inv_tel_quality')}>
                <ShieldCheck size={12} strokeWidth={2} style={{ color: qualityColor }} />
                <span className="capitalize" style={{ color: qualityColor }}>{data.qualityFlag}</span>
              </span>
              {effectiveCapKw != null && (
                <span className="inline-flex items-center gap-1.5" title={t('inv_tel_cap')}>
                  <Gauge size={12} strokeWidth={2} />
                  <span className="tabular-nums">{effectiveCapKw} кВт</span>
                </span>
              )}
            </div>
          </div>


          {/* Inverter groups (multi-protocol sites — shown when 2+ groups exist) */}
          {groups.length > 1 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold" style={{ color: textMain }}>
                  {t('inv_tel_groups')}
                </h4>
                <span className="text-xs" style={{ color: textMuted }}>
                  {groups.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {groups.map((g) => (
                  <InverterGroupCard key={g.key} group={g} />
                ))}
              </div>
            </div>
          )}

          {/* Inverter grid — only legacy single-group sites */}
          {groups.length <= 1 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold" style={{ color: textMain }}>
                {t('inv_tel_groups')}
              </h4>
              <span className="text-xs" style={{ color: textMuted }}>
                {t('inv_tel_units_count', { count: inverters.length })}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {inverters.map((inv) => (
                <InverterCard key={inv.unitId} inv={inv} />
              ))}
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}